import { RequestResponse } from '@/common/types_and_interfaces.ts'
import { parseRequestError } from '@/common/utilities.ts'
import axios from 'axios'
import { z } from 'zod'
import {
  FederalPovertyLevelProps,
  FederalPovertyLevelSchema,
  GlobalProgramsResponse,
  GlobalProgramsResponseProps,
  ProgramYearsResponse,
  ProgramYearsResponseProps,
} from './schemas.ts'

const parseDate = (dateString: string | null | undefined) => {
  if (!dateString) return undefined

  const parsedDate = new Date(dateString)

  return isNaN(parsedDate.getTime()) ? undefined : parsedDate
}

const ProgramDetailsSchema = z.object({
  id: z.number(),
  status: z.number(),
  name: z.string().nullable(),
  global_state_id: z.number().nullable(),
  enrollment_start_date: z.string().transform(parseDate),
  enrollment_end_date: z.string().transform(parseDate),
  new_application_start_date: z.string().transform(parseDate).nullable(),
  new_application_end_date: z.string().transform(parseDate).nullable(),
  program_start_date: z.string().transform(parseDate),
  program_end_date: z.string().transform(parseDate),
  acceptable_income_year: z.string(),
  manager_id: z.number().nullable(),
  program_image: z.string().nullable(),
  created_by: z.string().nullable(),
  created_datetime: z.string().transform(parseDate),
  state_name: z.string().nullable(),
  verify_income: z.number().optional(),
  class_wallet: z.number().optional(),
  unrulr: z.number().optional(),
  priority_level: z.number().nullable(),
  income_verification: z.string().nullable(),
  acceptable_documents: z.string().nullable(),
  documents_fields: z.string().nullable(),
  federal_poverty_levels: z.array(FederalPovertyLevelSchema).nullable(),
  program_appropiation_amount: z.string().nullable(),
  awarded_scholarships_number: z.string().nullable(),
  reward_amount: z.string().nullable(),
  minimum_age: z.number().nullable(),
  maximum_age: z.number().nullable(),
  residency_state: z.number().nullable(),
  cut_off_date: z.string().transform(parseDate).nullable(),
  military_exception: z.number().optional(),
  allow_manual_achievement_verification: z.number(),
  achievement_option_ids: z.array(z.number()).min(0),
  appeals_window: z.number().nullable(),
  marketplace_module: z.number().optional(),
  send_bill_module: z.number().optional(),
  reimbursement_module: z.number().optional(),
  require_family_bank_info_for_reimbursement: z.number().optional(),
  pay_provider_module: z.number().optional(),
  require_provider_bank_info_for_transactions: z.number().optional(),
  item_sale_tax_enabled: z.number().optional(),
  service_fee_charged_to: z.number().optional(),
  service_fee: z.string().nullable(),
})

export type ProgramDetails = z.infer<typeof ProgramDetailsSchema>

export const GetProgramDetail = async (
  programId?: number
): Promise<ProgramDetails> => {
  try {
    const response = await axios.get(`programs/${programId}`)
    return ProgramDetailsSchema.parse(response.data)
  } catch (e) {
    throw new Error(parseRequestError(e).message)
  }
}

const UserRolesSchema = z.enum(['committee'])
export type UserRoles = z.infer<typeof UserRolesSchema>

const ProgramUsersSchema = z.object({
  id: z.number(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  roles: z.string().nullable(),
})

const ProgramUsersListSchema = z.array(ProgramUsersSchema) // Expect an array directly from the API
export type ProgramUsersList = z.infer<typeof ProgramUsersListSchema>

export const GetProgramUsers = async (
  programId?: number,
  roles?: UserRoles[]
): Promise<ProgramUsersList> => {
  try {
    const response = await axios.get(`users/program/${programId}?paginate=0`, {
      params: {
        roles: roles,
      },
    })
    return ProgramUsersListSchema.parse(response.data)
  } catch (e) {
    throw new Error(parseRequestError(e).message)
  }
}

const ProgramListSchema = z.array(ProgramDetailsSchema) // Expect an array directly from the API
export type UserProgramList = z.infer<typeof ProgramListSchema>

export const GetUserPrograms = async (
  userId?: number
): Promise<UserProgramList> => {
  try {
    const response = await axios.get(`users/${userId}/enrolled_programs`)
    return ProgramListSchema.parse(response.data?.data)
  } catch (e) {
    throw new Error(parseRequestError(e).message)
  }
}

// update Program
type UpdateProgramReq = {
  program_image_path?: string | null
  name: string
  enrollment_start_date: string | null | undefined
  enrollment_end_date: string | null | undefined
  new_application_start_date: string | null | undefined
  new_application_end_date: string | null | undefined
  program_start_date: string | null | undefined
  program_end_date: string | null | undefined
  acceptable_income_year: string | undefined
  global_state_id: number | null
  manager_id: number | null | undefined
  program_image: File | null | undefined | string
  class_wallet: number | null | undefined
  verify_income: number | null | undefined
  unrulr: number | null | undefined
  priority_level?: number | null
  income_verification?: string | null
  acceptable_documents?: string | null
  documents_fields?: string | null
  federal_poverty_levels?: Array<FederalPovertyLevelProps> | null
  program_appropiation_amount: string | null | undefined
  reward_amount: string | null | undefined
  awarded_scholarships_number: string | null | undefined
  minimum_age: number | null | undefined
  maximum_age: number | null | undefined
  cut_off_date?: string | null
  residency_state: number | null | undefined
  military_exception: number | null | undefined
  allow_manual_achievement_verification: number | undefined
  achievement_option_ids: number[] | undefined
  appeals_window: number | null | undefined
  marketplace_module: number | null | undefined
  send_bill_module: number | null | undefined
  reimbursement_module: number | null | undefined
  require_family_bank_info_for_reimbursement: number | null | undefined
  pay_provider_module: number | null | undefined
  require_provider_bank_info_for_transactions: number | null | undefined
  item_sale_tax_enabled: number | null | undefined
  service_fee_charged_to: number | null | undefined
  service_fee?: string | null
  academic_term_type_id?: number
}

const UpdateProgramRespSchema = z.object({
  id: z.number(),
  academic_term_type_id: z.number().nullable(),
})

export type V2UpdateProgramPayload = {
  name: string
  global_state_id: number
  enrollment_start_date: string
  enrollment_end_date: string
  program_start_date: string
  program_end_date: string

  academic_term_type_id?: number
}

/**
 * V2 Update Program - Safe partial update implementation
 *
 * This function replaces the legacy `UpdateProgram` mutation which has unsafe default behavior.
 * The legacy implementation falls back to default values for missing fields, risking unintended
 * data modifications. This V2 implementation only updates fields explicitly provided in the payload,
 * ensuring safer partial updates without side effects.
 */
export const V2UpdateProgram = async (
  programId: number,
  requestBody: V2UpdateProgramPayload
) => {
  try {
    const response = await axios.put(`programs/${programId}`, requestBody)
    return UpdateProgramRespSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export type UserInviteResp = z.infer<typeof UpdateProgramRespSchema>

export const UpdateProgram = async (
  requestBody: UpdateProgramReq,
  id: number
): Promise<RequestResponse<UserInviteResp>> => {
  try {
    const formData = new FormData()
    formData.append('_method', 'PUT')
    formData.append('name', requestBody.name ?? '')
    formData.append(
      'enrollment_start_date',
      requestBody.enrollment_start_date ?? ''
    )
    formData.append(
      'enrollment_end_date',
      requestBody.enrollment_end_date ?? ''
    )

    if (requestBody.academic_term_type_id) {
      formData.append(
        'academic_term_type_id',
        String(requestBody.academic_term_type_id)
      )
    }
    if (requestBody.new_application_start_date)
      formData.append(
        'new_application_start_date',
        requestBody.new_application_start_date ?? ''
      )

    if (requestBody.new_application_end_date)
      formData.append(
        'new_application_end_date',
        requestBody.new_application_end_date ?? ''
      )
    if (requestBody.acceptable_income_year) {
      formData.append(
        'acceptable_income_year',
        requestBody.acceptable_income_year
      )
    }
    formData.append('program_start_date', requestBody.program_start_date ?? '')
    formData.append('program_end_date', requestBody.program_end_date ?? '')
    formData.append(
      'global_state_id',
      String(requestBody.global_state_id) ?? ''
    )
    if (requestBody.manager_id) {
      formData.append('manager_id', String(requestBody.manager_id))
    }
    if (requestBody.program_image) {
      formData.append('program_image', requestBody.program_image)
    } else if (!requestBody.program_image_path) {
      formData.append('program_image_path', requestBody.program_image ?? '')
    }
    if (
      requestBody.class_wallet !== undefined &&
      requestBody.class_wallet !== null
    ) {
      formData.append('class_wallet', String(requestBody.class_wallet))
    }
    if (
      requestBody.verify_income !== undefined &&
      requestBody.verify_income !== null
    ) {
      formData.append('verify_income', String(requestBody.verify_income))
    }
    if (
      requestBody?.program_appropiation_amount &&
      requestBody.program_appropiation_amount.trim().length > 0
    )
      formData.append(
        'program_appropiation_amount',
        requestBody.program_appropiation_amount
      )

    if (
      requestBody?.reward_amount &&
      requestBody.reward_amount.trim().length > 0
    )
      formData.append('reward_amount', requestBody.reward_amount)

    if (
      requestBody?.awarded_scholarships_number &&
      requestBody.awarded_scholarships_number.trim().length > 0
    )
      formData.append(
        'awarded_scholarships_number',
        requestBody.awarded_scholarships_number
      )

    formData.append('minimum_age', String(requestBody.minimum_age))
    formData.append('maximum_age', String(requestBody.maximum_age))
    formData.append(
      'military_exception',
      String(requestBody.military_exception ?? 0)
    )
    formData.append(
      'allow_manual_achievement_verification',
      String(requestBody.allow_manual_achievement_verification ?? 0)
    )
    formData.append(
      'achievement_option_ids',
      JSON.stringify(requestBody.achievement_option_ids ?? [])
    )

    formData.append('appeals_window', String(requestBody.appeals_window ?? '0'))

    if (requestBody.cut_off_date)
      formData.append('cut_off_date', String(requestBody.cut_off_date))
    if (requestBody.residency_state)
      formData.append('residency_state', String(requestBody.residency_state))

    if (requestBody.unrulr !== undefined && requestBody.unrulr !== null) {
      formData.append('unrulr', String(requestBody.unrulr))
    }
    if (requestBody.priority_level) {
      formData.append('priority_level', String(requestBody.priority_level))
    }
    if (requestBody.income_verification) {
      formData.append(
        'income_verification',
        String(requestBody.income_verification)
      )
    }
    if (requestBody.acceptable_documents) {
      formData.append(
        'acceptable_documents',
        String(requestBody.acceptable_documents)
      )
    }
    if (requestBody.documents_fields)
      formData.append('documents_fields', String(requestBody.documents_fields))
    if (requestBody.federal_poverty_levels) {
      formData.append(
        'federal_poverty_levels',
        JSON.stringify(requestBody.federal_poverty_levels)
      )
    }
    formData.append(
      'marketplace_module',
      String(requestBody.marketplace_module ?? 0)
    )
    formData.append(
      'send_bill_module',
      String(requestBody.send_bill_module ?? 0)
    )
    formData.append(
      'reimbursement_module',
      String(requestBody.reimbursement_module ?? 0)
    )
    formData.append(
      'require_family_bank_info_for_reimbursement',
      String(requestBody.require_family_bank_info_for_reimbursement ?? 0)
    )
    formData.append(
      'pay_provider_module',
      String(requestBody.pay_provider_module ?? 0)
    )
    formData.append(
      'require_provider_bank_info_for_transactions',
      String(requestBody.require_provider_bank_info_for_transactions ?? 0)
    )
    formData.append(
      'item_sale_tax_enabled',
      String(requestBody.item_sale_tax_enabled ?? 0)
    )
    formData.append(
      'service_fee_charged_to',
      String(requestBody.service_fee_charged_to ?? 0)
    )
    formData.append('service_fee', String(requestBody.service_fee ?? '0'))
    const response = await axios.post(`programs/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data', // Ensure the header is set for form data
      },
    })
    const cleanResponse = UpdateProgramRespSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

export const getGlobalPrograms =
  async (): Promise<GlobalProgramsResponseProps> => {
    try {
      const response = await axios.get(`global_programs`)
      return GlobalProgramsResponse.parse(response.data)
    } catch (e) {
      throw new Error(parseRequestError(e).message)
    }
  }

export const getYearsProgramQuery =
  async (): Promise<ProgramYearsResponseProps> => {
    try {
      const response = await axios.get(`programs`)
      return ProgramYearsResponse.parse(response.data)
    } catch (e) {
      throw new Error(parseRequestError(e).message)
    }
  }
